import {
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { LockScope } from 'components/FieldLock/types'
import { InputType, INVALID_ALL_INPUTS } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { svgLoading } from 'stories/assets'
import {
  confirm,
  formatDate,
  getPrice3decimal,
  InputConvert,
  InputValidate,
  isHTML,
  multiSelectRender,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { Button } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import { Modal } from '../Modal/Modal'
import { OverrideCaclModal } from '../OverrideCalcModal/OverrideCalcModal'
import { Pagination } from '../Pagination/Pagination'

export interface FormTableHeader {
  key: string
  title: string | JSX.Element
  value?: Function
  prefix?: string
  sortable?: boolean
  /**
   * What this column sorts on, when the cell itself is not comparable - a composite cell built
   * as JSX, or an amount rendered as a formatted string. Return a number for numeric columns so
   * they order by value rather than by digit.
   */
  sortValue?: (row: Record<string, any>) => string | number
  /**
   * Renders the cell outright, in place of the type-driven formatting below (thousandSep, date,
   * select label, `$` prefix). Use it where a column needs its own money or date format.
   */
  format?: (row: Record<string, any>) => string
}

interface FormTableProps {
  /**
   * Header of Table
   */
  header: Array<FormTableHeader>
  /**
   * Table data
   */
  data?: Array<Record<string, any>> | null
  /**
   * Table default data
   */
  defaultData?: Array<Record<string, any>> | null
  /**
   * Inputs
   */
  seperateRowKey?: string
  addText?: string
  /** Set when the page renders its own add button via the ref's openAdd(). */
  externalAddButton?: boolean
  cancelEditFlag?: number
  disableForValidation?: boolean
  type?: string
  inputs?: Function | Record<string, InputType>
  cols?: number
  // 1: Full Permission
  // 1.5: Can Not Delete, Can Update, Can Submit
  // 2: Can Not Delete, Can Not Update, Can Submit
  // 2.5: Can Not Delete, Can Not Submit, Can Update
  // 3: Can Not Delete, Can Not Submit, Can Not Update
  // 4: Can Delete, Can Update, Can Not Submit
  permission?: number
  showModal?: boolean
  /** When set with showModal, replaces the default "Add New" / "Updating No. …" title. */
  modalTitle?: string
  submitText?: string
  /** Label for the submit button when editing an existing row (default: Update). */
  updateSubmitText?: string
  noText?: string
  calcKeys?: string[]
  calcVals?: Record<string, Record<string, any>>
  canOverrideCalc?: boolean

  renderAdditionalActions?: ((data: Record<string, any>, index: number) => JSX.Element) | null
  /**
   * Something to show above the form itself, inside the modal when the form is modal.
   *
   * For context the person filling the form needs in front of them rather than behind it - what a
   * draw is being entered against, say. `currentId` is 0 while adding and the row's id while
   * editing, so a caller can show one thing for a new row and another for an existing one.
   */
  renderFormHeader?: ((currentId: any) => JSX.Element | null) | null
  renderRawDescription?: ((data: Record<string, any>, index: number) => JSX.Element) | null
  initRow?: ((data: Record<string, any>, index: number) => Record<string, any>) | null
  onSubmit?: ((id: any, data: Record<string, any>, finishEditing?: boolean) => Record<string, any>) | null
  onRemove?: ((id: any, index: number) => void) | null
  onChange?: ((data: Record<string, any>[]) => void) | null
  onChangeInputs?: ((data: Record<string, InputType>, key: string) => void) | null
  onCancel?: Function | null
  onAddPress?: Function | null
  inputLogic?:
    | ((
        data: Record<string, InputType>,
        currentId?: number,
        currentKey?: string,
        values?: Record<string, any>,
      ) => Record<string, any>)
    | null
  showHistory?: ((headers: Array<FormTableHeader>) => void) | null
  showFieldHistory?: ((id: number, key: string) => void) | null
  canOverride?: boolean
  lockScopeForKey?: ((key: string) => LockScope | undefined) | null
  sectionLocked?: boolean
  /** Column the grid opens sorted on. The header still has to be `sortable` to be clickable. */
  defaultSortKey?: string
  /** Direction for `defaultSortKey`: 1 ascending, -1 descending. */
  defaultSortOrder?: 1 | -1
  /** Pin the Action column to the right edge, so a wide grid keeps its row actions in view. */
  stickyActions?: boolean
  /**
   * Page the rows, with the per-page select Pagination already provides. Off by default: every
   * other grid built on this component shows all of its rows, and a few of them derive running
   * totals from what is on screen.
   */
  hasPagination?: boolean
  /** Rows per page to open on, when `hasPagination` is set. The reader can change it. */
  itemCountPerPage?: number
  /** When true, show select-all + per-row checkboxes keyed by row.id. */
  selectable?: boolean
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
}

// A pinned Action cell needs an opaque background or the columns scrolling under it show through,
// and a shadow rather than a border: a collapsed-table border stays behind when the cell sticks.
const STICKY_ACTION_CLASS = 'sticky right-0 z-10 shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.25)]'

const _FormTable = (
  {
    header = [],
    data: _data = null,
    defaultData = null,
    inputs: _inputs = {},
    cols = 2,
    permission = 1,
    initRow = null,
    inputLogic = null,
    seperateRowKey = '',
    addText = '',
    externalAddButton = false,
    cancelEditFlag = 0,
    disableForValidation = false,
    showModal = false,
    modalTitle,
    submitText = 'Submit',
    updateSubmitText = 'Update',
    renderAdditionalActions = null,
    renderFormHeader = null,
    renderRawDescription = null,
    onSubmit: _onSubmit = null,
    onRemove: _onRemove = null,
    onChange: _onChange = null,
    onChangeInputs: _onChangeInputs = null,
    onCancel: _onCancel = null,
    onAddPress: _onAddPress = null,
    showHistory: _showHistory = null,
    showFieldHistory = null,
    noText = '',
    canOverride = false,
    calcKeys = [],
    calcVals,
    canOverrideCalc,
    lockScopeForKey = null,
    sectionLocked = false,
    defaultSortKey,
    defaultSortOrder = 1,
    stickyActions = false,
    hasPagination = false,
    itemCountPerPage: initialCountPerPage = 10,
    selectable = false,
    selectedIds = [],
    onSelectionChange,
  }: FormTableProps,
  ref: React.Ref<any>,
) => {
  const [loading, setLoading] = useState(false)
  const [currentId, setCurrentId] = useState(0)
  const [data, setData] = useState<Array<Record<string, any>>>([])
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  // A date field publishes its value on blur, and that blur is the click on Submit. The click
  // handler still sees the previous render, so keep the latest inputs here and read them after blur.
  const inputsRef = useRef(inputs)
  useEffect(() => {
    inputsRef.current = inputs
  }, [inputs])
  const [status, setStatus] = useState(0)
  const [ovData, setOVData] = useState<any>({})
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null)
  const [sortOrder, setSortOrder] = useState<1 | -1>(defaultSortOrder)
  const [pageNum, setPageNum] = useState(0)
  const [countPerPage, setCountPerPage] = useState(initialCountPerPage)

  const { hasPermission } = usePermissions()

  const initInputs = () => {
    if (typeof _inputs === 'function') setInputs(_inputs())
    else setInputs(_inputs)
  }

  const onSort = (key: string) => {
    if (!key) return
    if (sortKey === key) {
      setSortOrder(-sortOrder as 1 | -1)
    } else {
      setSortKey(key)
      setSortOrder(1)
    }
  }

  /**
   * Display order. The rows themselves are left alone: anything derived from `data` - a running
   * balance, a closing total - is computed by the caller in the order it handed the rows over,
   * so sorting here only ever reorders what is shown.
   */
  const sortedData = useMemo(() => {
    if (!sortKey) return data
    const column = header.find((item) => item.key === sortKey)
    const valueOf = (row: Record<string, any>) => (column?.sortValue ? column.sortValue(row) : row[sortKey])
    const givenOrder = new Map(data.map((row, index) => [row, index]))
    // Copy first - Array.prototype.sort works in place, and `data` is state.
    return [...data].sort((a, b) => {
      const valueA = valueOf(a)
      const valueB = valueOf(b)
      let diff = 0
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        diff = valueA === valueB ? 0 : valueA > valueB ? 1 : -1
      } else {
        const stringA = String(valueA ?? '').toUpperCase()
        const stringB = String(valueB ?? '').toUpperCase()
        diff = stringA === stringB ? 0 : stringA > stringB ? 1 : -1
      }
      // Ties fall back to the order the rows were given in, and that fallback turns with the
      // direction: descending has to be the exact reverse of ascending. Left un-reversed, several
      // rows sharing a sort value read bottom-up, so on a ledger the topmost row of the newest day
      // shows a mid-day running balance instead of the day's closing one.
      if (!diff) diff = (givenOrder.get(a) ?? 0) - (givenOrder.get(b) ?? 0)
      return diff * sortOrder
    })
  }, [data, header, sortKey, sortOrder])

  /**
   * The slice on screen, and where it starts in `sortedData`.
   *
   * Every row index the body passes on - the displayed number, the edit highlight, the row
   * actions - stays an index into `sortedData`, so it keeps meaning the same thing whether or
   * not paging is on. Taking the index from the sliced array instead would restart the numbering
   * at 1 on every page.
   */
  const pageStart = hasPagination ? pageNum * countPerPage : 0
  const pagedData = useMemo(() => {
    if (!hasPagination) return sortedData
    return sortedData.slice(pageStart, pageStart + countPerPage)
  }, [sortedData, hasPagination, pageStart, countPerPage])

  // Deleting the last rows of the last page, or showing more per page, can leave the reader on a
  // page that no longer exists - which renders as an empty grid.
  useEffect(() => {
    if (!hasPagination) return
    const lastPage = Math.max(0, Math.ceil(sortedData.length / countPerPage) - 1)
    if (pageNum > lastPage) setPageNum(lastPage)
  }, [hasPagination, sortedData.length, countPerPage, pageNum])

  // Position of the row being edited, in the order the grid shows it, so the "Updating No."
  // label and the highlighted row name the same line the user clicked.
  const currentIndex = useMemo(() => {
    if (!currentId) return null
    const index = sortedData.findIndex((item) => item.id == currentId)
    if (index == -1) return null
    return index
  }, [sortedData, currentId])

  useEffect(() => {
    if (addText.length > 0) setStatus(-1)
  }, [addText])

  useEffect(() => {
    // Parent-driven dismiss (e.g. opening a vendor modal) must run the same cleanup as Cancel,
    // including onCancel — otherwise callers that sync filters to the edited row leave them stuck.
    if (cancelEditFlag > 0) onCancelEdit()
  }, [cancelEditFlag])

  useEffect(() => {
    initInputs()
  }, [_inputs])

  useEffect(() => {
    if (!defaultData || data.length) return
    if (initRow) {
      defaultData = defaultData.map((item, index) => {
        return initRow(item, index)
      })
    }
    setData(defaultData)
  }, [defaultData])

  useEffect(() => {
    if (!_data) return
    if (initRow) {
      _data = _data.map((item, index) => {
        return initRow(item, index)
      })
    }
    setData(_data)
  }, [_data])

  useImperativeHandle(ref, () => ({
    submit() {
      return onSubmit(false)
    },
    /** Enter add mode from parent template, then run onAddPress for caller defaults. */
    async openAdd() {
      const base = typeof _inputs === 'function' ? _inputs() : cloneDeep(_inputs)
      setInputs(base)
      if (_onAddPress && !(await _onAddPress())) return
      setStatus(0)
    },
    /**
     * Enter add mode with the form already filled in from `values`.
     *
     * Unlike openAdd + updateInputs, this goes through resetInputs, so `inputLogic` runs over the
     * prefilled values and everything derived from them is computed before the form is shown. A
     * caller prefilling an amount needs that: otherwise the fields calculated from it stay blank
     * until the user happens to touch something.
     */
    async openAddWith(values: Record<string, any>) {
      if (_onAddPress && !(await _onAddPress())) return
      await resetInputs(values, 0)
      setCurrentId(0)
      setStatus(0)
    },
    updateInputs(inputUpdates: Record<string, Record<string, any>>) {
      setInputs((prev) => {
        const newInputs = cloneDeep(prev)
        Object.keys(inputUpdates).forEach((key) => {
          newInputs[key] = { ...newInputs[key], ...inputUpdates[key] }
        })
        return newInputs
      })
    },
    /** Keep the open form and switch it to update mode for an order that was saved but not accepted. */
    stayInEdit(id: number) {
      if (!id) return
      setCurrentId(id)
      setStatus(0)
    },
  }))

  const resetInputs = async (values: Record<string, any> = {}, id: number) => {
    let newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = InputConvert(newInputs[key], values[key])
      newInputs[key].error = ''
    })
    if (inputLogic !== null) {
      const newLogic = inputLogic(newInputs, id, '', values)
      if (newLogic instanceof Promise) {
        setLoading(true)
        newInputs = await newLogic
        setLoading(false)
      } else newInputs = newLogic
    }
    setInputs(newInputs)
  }

  const onChange = async (key: string, value: string) => {
    let newInputs = cloneDeep(inputsRef.current)
    value = InputConvert(newInputs[key], value)

    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])
    inputsRef.current = newInputs
    if (inputLogic !== null) {
      const newLogic = inputLogic(newInputs, currentId, key)
      if (newLogic instanceof Promise) {
        setLoading(true)
        newInputs = await newLogic
        setLoading(false)
      } else newInputs = newLogic
      inputsRef.current = newInputs
    }
    setInputs(newInputs)
    _onChangeInputs && _onChangeInputs(newInputs, key)
  }

  /** `index` addresses `data`; `displayNo` is the number the grid is showing for that row. */
  const onRemove = async (id: any, index: number, displayNo = index + 1) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove <span className="font-bold">No.{displayNo}</span> item?
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    _onRemove && (await _onRemove(id, index))
    if (!_data) {
      const newData = cloneDeep(data)
      newData.splice(index, 1)
      setData(newData)
      _onChange && _onChange(newData)
    }
    onCancelEdit()
  }

  const onEdit = (row: Record<string, any>) => {
    if (currentId && row.id === currentId) {
      onCancelEdit()
      return
    }
    setCurrentId(row.id)
    setStatus(0)
    resetInputs(row, row.id)
  }

  const isFormValidated = useMemo(() => {
    if (!disableForValidation) return true

    let hasError = false
    Object.keys(inputs).forEach((key) => {
      const { visible } = inputs[key]
      if (visible && typeof visible === 'function' && !visible(inputs)) return null
      if (inputs[key].visible === false) return null
      if (InputValidate(inputs[key])) hasError = true
    })
    return !hasError
  }, [inputs])

  const onSubmit = async (finishEditing = true) => {
    const active = document.activeElement
    if (active instanceof HTMLElement) active.blur()
    const inputs = inputsRef.current
    const newInputs = cloneDeep(inputs)
    let values: Record<string, any> = {}
    let hasError = false
    Object.keys(inputs).forEach((key) => {
      const { visible } = inputs[key]
      if (visible && typeof visible === 'function' && !visible(inputs)) return null
      if (inputs[key].visible === false) return null
      newInputs[key].error = InputValidate(newInputs[key])
      const { value, error } = newInputs[key]
      if (error) hasError = true
      values[key] = value
    })
    setInputs(newInputs)

    if (hasError) {
      let result = false
      if (canOverride)
        result = (await confirm('Are you sure you want to override saving required fields?', {
          titleNo: `No, I don't`,
        })) as any
      if (!result) {
        toast(INVALID_ALL_INPUTS, { type: 'error' })
        return false
      }
    }

    setLoading(true)
    if (_onSubmit) {
      try {
        values = await _onSubmit(currentId, values, finishEditing)
      } catch (e) {
        setLoading(false)
        return
      }
    } else
      values = {
        id: Date.now(),
        ...values,
      }
    if (finishEditing) {
      setCurrentId(0)
      initInputs()
    } else setCurrentId(values.id)

    setLoading(false)
    if (currentId) {
      if (!_data) {
        const newData = cloneDeep(data)
        const index = newData.findIndex((item) => item.id == currentId)
        newData[index] = {
          id: currentId,
          ...values,
        }
        setData(newData)
        _onChange && _onChange(newData)
      }
      finishEditing && setStatus(2)
    } else {
      if (!_data) {
        const newData = cloneDeep(data)
        newData.push(values)
        setData(newData)
        _onChange && _onChange(newData)
      }
      finishEditing && setStatus(1)
    }
    return values.id
  }

  const onCancelEdit = () => {
    setCurrentId(0)
    initInputs()
    if (addText.length > 0) {
      setStatus(-1)
    }
    if (_onCancel) _onCancel()
  }

  const closeOVModal = async (save: false, data: any) => {
    if (save) {
      onChange(ovData.overrideKey, data.ovValue)
      await onChange(ovData.overrideKey, data.ovValue)
    }
    setOVData({})
  }

  const getCanUpdateRow = () => {
    return data.find((item) => item.id == currentId)?.canUpdateRow || false
  }

  const renderAction = () => {
    if (permission === 3) return null
    if (currentId == 0) {
      if (permission == 2.5 || permission == 4) return null
      return (
        <>
          <Button disabled={!isFormValidated || sectionLocked} onClick={onSubmit} className="px-10" loading={loading}>
            {submitText}
          </Button>
          {addText.length > 0 && (
            <Button onClick={onCancelEdit} color="white" className="px-10 py-1">
              Cancel
            </Button>
          )}
        </>
      )
    }
    const canUpdateRow = getCanUpdateRow()
    return (
      <>
        <Button
          onClick={onSubmit}
          className="px-10 py-1"
          loading={loading}
          disabled={(![1, 1.5, 2.5, 4].includes(permission) && !canUpdateRow) || !isFormValidated || sectionLocked}
        >
          {updateSubmitText}
        </Button>
        <Button onClick={onCancelEdit} color="white" className="px-10 py-1">
          Cancel
        </Button>
      </>
    )
  }

  const headerActionFragment = useMemo(() => {
    if (permission === 3) return null
    return (
      <th scope="col" className={`px-2 py-3${stickyActions ? ` ${STICKY_ACTION_CLASS} bg-slate-50` : ''}`}>
        <span className="group flex items-center gap-2 justify-end">
          Action
          {hasPermission('Global.AuditLog') && _showHistory && (
            <span
              className="hidden group-hover:inline text-shade-blue dark:text-gray-400 cursor-pointer hover:underline text-[13px]"
              onClick={() => _showHistory(header)}
            >
              <ClockIcon className="w-4 h-4" aria-hidden="true" />
            </span>
          )}
        </span>
      </th>
    )
  }, [permission, header, _showHistory, hasPermission, stickyActions])

  const canAdd =
    permission !== 3 &&
    status === -1 &&
    addText.length > 0 &&
    permission !== 2.5 &&
    permission !== 4 &&
    !externalAddButton

  const renderAddButton = () => {
    if (!canAdd) return null
    return (
      <div className="flex justify-end mb-2">
        <button
          type="button"
          disabled={sectionLocked}
          className={`btn-link${sectionLocked ? ' opacity-40 cursor-not-allowed hover:no-underline' : ''}`}
          onClick={async () => {
            if (sectionLocked) return
            // Match openAdd: start from a clean template so prior edit/add values do not bleed.
            const base = typeof _inputs === 'function' ? _inputs() : cloneDeep(_inputs)
            setInputs(base)
            if (_onAddPress && !(await _onAddPress())) return
            setStatus(0)
          }}
        >
          + Add
        </button>
      </div>
    )
  }
  const renderActionTD = (noRowSpans: number, row: any, index: number) => {
    // `index` is the row's position on screen, which is not its position in `data` once the
    // grid is sorted. Removal splices `data`, so it needs the source index.
    const dataIndex = data.indexOf(row)
    if (permission === 3) return null
    // Pinned, the cell paints its own background, matching the highlight on the row being edited.
    const tdClass = stickyActions
      ? `px-2 py-2 ${STICKY_ACTION_CLASS} ${currentIndex === index ? 'bg-gray-100' : 'bg-white'}`
      : 'px-2 py-2'
    // A row the grid does not own - one mirrored in from another record - is read here and
    // edited at its source, so it gets no actions.
    if (row?.rowLocked) return <td className={tdClass} rowSpan={noRowSpans} />
    return (
      <td className={tdClass} rowSpan={noRowSpans}>
        <div className="flex gap-1">
          <span
            className={`text-shade-blue p-1 rounded ${
              sectionLocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover-shadow1'
            }`}
            onClick={() => !sectionLocked && onEdit(row)}
          >
            <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
          </span>
          {(permission === 1 || permission === 4) && (
            <span
              className={`text-red-800 p-1 rounded ${
                sectionLocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover-shadow1'
              }`}
              onClick={() => !sectionLocked && onRemove(row.id, dataIndex, index + 1)}
            >
              <TrashIcon className="w-4 h-4"></TrashIcon>
            </span>
          )}
        </div>
        <div className="flex">{renderAdditionalActions && renderAdditionalActions(row, index)}</div>
      </td>
    )
  }

  const renderEditSection = () => {
    if (permission === 3) return null
    if (status === -1) {
      // The "add" button now lives at the top-right of the grid (see main return).
      return null
    }

    if (status === 1) {
      //After Adding Success
      return (
        <div
          className="mx-2 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 text-[15px] flex flex-wrap items-center"
          role="alert"
        >
          <CheckCircleIcon className="w-6 h-6"></CheckCircleIcon>
          <span className="ml-1">Successfully Added!</span>
          <span className="font-variation-settings-600 ml-3 mr-1 cursor-pointer underline" onClick={() => setStatus(0)}>
            Click Here
          </span>
          to create new item.
        </div>
      )
    }

    if (status === 2) {
      //After Update Success
      return (
        <div
          className="mx-2 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 text-[15px] flex flex-wrap items-center"
          role="alert"
        >
          <CheckCircleIcon className="w-6 h-6"></CheckCircleIcon>
          <span className="ml-1">Successfully Updated!</span>
          {permission != 2.5 && permission != 4 && (
            <>
              <span
                className="font-variation-settings-600 ml-3 mr-1 cursor-pointer underline"
                onClick={() => setStatus(0)}
              >
                Click Here
              </span>
              to create new item.
            </>
          )}
        </div>
      )
    }

    if (status === 0) {
      if ((permission == 2.5 || permission == 4) && currentIndex === null) return null

      const content = (
        <div className="px-4">
          {permission !== 3 && (
            <div className={`grid gap-4 md:grid-cols-${cols} grid-cols-1 mb-3`}>
              {Object.keys(inputs).map((key, index) => {
                const input: any = cloneDeep(inputs[key])
                if (sectionLocked) input.disabled = true
                if (typeof input.visible === 'function' && !input.visible(inputs)) return null
                if (input.visible === false) return null
                if (calcKeys.includes(key) && calcVals?.[currentId]?.[key]) {
                  input.disabled = true
                  if (canOverrideCalc) {
                    input.additionalElements = (
                      <span
                        className="text-shade-blue cursor-pointer"
                        onClick={() => {
                          setOVData({
                            title: input.title,
                            overrideKey: key,
                            calcValue: calcVals?.[currentId]?.[key] || '',
                            ovValue: input.value || '',
                          })
                        }}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                    )
                  }
                  input.value = input.value || calcVals?.[currentId]?.[key]
                  if (input.value != calcVals?.[currentId]?.[key]) input.bgColor = 'red-200'
                }

                if (input.inputType === 'divider') {
                  return (
                    <div className={`input md:col-span-${cols} -mx-2`} key={index}>
                      <div className="border p-2 rounded bg-gray-100 font-variation-settings-600 text-[14px]">
                        {input.title}
                      </div>
                    </div>
                  )
                }
                if (input.inputType === 'alert') {
                  return (
                    <div className={`input md:col-span-${cols}`} key={index}>
                      <div
                        className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative text-[15px]"
                        role="alert"
                      >
                        <span dangerouslySetInnerHTML={{ __html: input.value }} />
                      </div>
                    </div>
                  )
                }
                if (permission === 2 && currentIndex !== null) {
                  if (!getCanUpdateRow() && !input.alwaysEditable) input.disabled = true
                }

                if (typeof input.value === 'function') input.value = input.value(inputs)

                if (input.isLoading)
                  input.additionalElements = (
                    <img src={svgLoading} className="w-[14px] h-[14px] text-white animate-spin" />
                  )

                return (
                  <div className={`input md:col-span-${input.span || 1}`} key={index}>
                    <RenderInput
                      input={input}
                      Key={key}
                      onChange={onChange}
                      showHistory={(key: string) => showFieldHistory && showFieldHistory(currentId, key)}
                      lockScope={lockScopeForKey ? lockScopeForKey(key) : undefined}
                    />
                  </div>
                )
              })}
            </div>
          )}
          <div className="text-center flex justify-center mt-4">{renderAction()}</div>
        </div>
      )
      const formHeader = renderFormHeader ? renderFormHeader(currentId) : null

      if (!showModal)
        return (
          <div>
            {currentIndex !== null ? (
              <p className="text-md mb-3 border-b text-shade-blue ml-2">Updating No. {currentIndex + 1}</p>
            ) : null}
            {formHeader}
            {content}
          </div>
        )

      return (
        <Modal
          title={modalTitle ?? (currentIndex !== null ? `Updating No. ${currentIndex + 1}` : 'Add New')}
          titleOkay=""
          titleCancel=""
          hiddenX
          isOpen
          loading={loading}
        >
          <div className="w-196">
            {formHeader}
            {content}
          </div>
        </Modal>
      )
    }
  }

  return (
    <div className="Formtable-container">
      {renderAddButton()}
      <div className="overflow-auto mb-6 shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              {selectable && (
                <th scope="col" className="px-2 py-3 w-10">
                  <Checkbox
                    id="formtable-select-all"
                    title=""
                    disabled={!data.length}
                    value={
                      data.length > 0 &&
                      data.every((row) => row?.id != null && selectedIds.includes(String(row.id)))
                    }
                    onChange={(checked) => {
                      if (!onSelectionChange) return
                      onSelectionChange(
                        checked
                          ? data.map((row) => String(row.id || '')).filter(Boolean)
                          : [],
                      )
                    }}
                  />
                </th>
              )}
              <th scope="col" className="px-2 py-3">
                {noText || 'No'}
              </th>
              {header.map((item) => {
                const active = sortKey === item.key
                return (
                  <th
                    scope="col"
                    className={`px-2 py-3 ${item?.sortable && 'cursor-pointer'}`}
                    key={item.key}
                    onClick={() => {
                      if (item?.sortable) onSort(item.key)
                    }}
                  >
                    <div className={item?.sortable ? 'flex item-center gap-1' : ''}>
                      {typeof item.title === 'string' ? (
                        <span dangerouslySetInnerHTML={{ __html: item.title }} />
                      ) : (
                        item.title
                      )}
                      {item?.sortable &&
                        (active && sortOrder === 1 ? (
                          <BarsArrowUpIcon className="w-3 h-3 ml-2" />
                        ) : active && sortOrder === -1 ? (
                          <BarsArrowDownIcon className="w-3 h-3 ml-2" />
                        ) : (
                          <div className="w-3 h-3 ml-2" />
                        ))}
                    </div>
                  </th>
                )
              })}
              {headerActionFragment}
            </tr>
          </thead>
          <tbody className="text-[14px] text-gray-900">
            {pagedData.map((row, rowOnPage: number) => {
              const index = pageStart + rowOnPage
              let noRowSpans = 1
              let showSecondRow = false
              if (row[seperateRowKey]?.length > 0) {
                showSecondRow = true
              }
              if (showSecondRow) noRowSpans = 2
              let trBorderColor = currentIndex === index ? 'bg-gray-100' : ''
              return [
                renderRawDescription && renderRawDescription(row, index),
                <tr key={index} className={`border-b ${trBorderColor} ${row.tdClass}`}>
                  {selectable && (
                    <td rowSpan={noRowSpans} className="px-2 py-2">
                      <Checkbox
                        id={`formtable-row-${row.id || index}`}
                        title=""
                        value={selectedIds.includes(String(row.id || ''))}
                        onChange={(checked) => {
                          if (!onSelectionChange) return
                          const id = String(row.id || '')
                          if (!id) return
                          if (checked) {
                            onSelectionChange(
                              selectedIds.includes(id) ? selectedIds : [...selectedIds, id],
                            )
                          } else {
                            onSelectionChange(selectedIds.filter((item) => item !== id))
                          }
                        }}
                      />
                    </td>
                  )}
                  <td rowSpan={noRowSpans} className="px-2 py-2 pl-3 font-medium text-gray-900 whitespace-nowrap">
                    {index + 1}
                  </td>
                  {header.map((header, rIndex) => {
                    // A column with its own formatter renders outright: the type-driven
                    // formatting below would re-parse what it produced.
                    if (header.format)
                      return (
                        <td className="px-2 py-2" key={rIndex}>
                          <span className="">{header.format(row)}</span>
                        </td>
                      )
                    let rlt = header.value ? header.value(row) : row[header.key]
                    let options = (inputs[header.key] as any)?.options
                    if (rlt === undefined) rlt = ''
                    if (inputs[header.key]?.type === 'thousandSep' && rlt !== undefined) rlt = getPrice3decimal(rlt)
                    if (inputs[header.key]?.type === 'date' && rlt !== undefined) rlt = formatDate(rlt)
                    if (
                      inputs[header.key]?.inputType === 'select' &&
                      rlt !== undefined &&
                      typeof options === 'object' &&
                      !Array.isArray(options) &&
                      !header.value
                    )
                      rlt = options[rlt]
                    if (inputs[header.key]?.inputType === 'multiselect' && rlt !== undefined && !header.value)
                      rlt = multiSelectRender(rlt)
                    if (typeof rlt == 'boolean') rlt = rlt ? 'Yes' : 'No'
                    if (inputs[header.key]?.prefix === '$') rlt = `$${rlt}`
                    if (header.prefix) rlt = `${header.prefix}${rlt}`
                    return (
                      <td className="px-2 py-2" key={rIndex}>
                        {isHTML(rlt) ? (
                          <span className="dangerouslySetInnerHTML">
                            <div dangerouslySetInnerHTML={{ __html: rlt }}></div>
                          </span>
                        ) : (
                          <span className="">{rlt}</span>
                        )}
                      </td>
                    )
                  })}
                  {renderActionTD(noRowSpans, row, index)}
                </tr>,
                showSecondRow && (
                  <tr className={`border-b ${trBorderColor}`} key={`1-${index}`}>
                    <td className="px-2 py-2" colSpan={header.length}>
                      <span className="italic">{row[seperateRowKey]}</span>
                    </td>
                  </tr>
                ),
              ]
            })}
          </tbody>
        </table>
      </div>
      {hasPagination && sortedData.length > 0 && (
        <Pagination
          totalCount={sortedData.length}
          itemCountPerPage={countPerPage}
          pageNum={pageNum}
          onNavigate={setPageNum}
          onCountPerPageUpdate={(count: number) => {
            setCountPerPage(count)
            setPageNum(0)
          }}
        />
      )}
      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          onClose={closeOVModal}
        />
      )}

      {renderEditSection()}
    </div>
  )
}

export const FormTable = forwardRef(_FormTable)
